import {
    ButtonGroup,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    TableCell,
    Tooltip,
    Typography
} from "@mui/material";
import React, {createContext, useCallback, useContext, useMemo, useState} from "react";
import {VirtualTable} from "../../components/data-table/VirtualTable";
import {TableColumnData} from "../../components/data-table/TableColumnData";
import {KeyLocationShield} from "../../components/location/KeyLocationShield";
import {KeyData} from "../../api/Model";
import {idFn} from "../../util/FunctionUtil";
import {KeyTagChip} from "../../components/key-tag-chip/KeyTagChip";
import {KeyChip} from "../../components/key-chip/KeyChip";
import {useKeyHistoryDialog} from "../history-dialog/HistoryDialogContext";
import {activeRecord} from "../../api/KeyDataStore";
import {KeyProblems} from "../../api/KeyProblemStore";
import {KeyProblemIcon} from "./KeyProblemIcon";
import {KeyHistoryPeriod} from "../../components/key-history-period/KeyHistoryPeriod";
import UpdateIcon from "@mui/icons-material/Update";
import KeyOperationsIcon from '@mui/icons-material/SwapHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {KeyOperationDialog} from "./KeyOperationDialog";

function KeyErrorCell({keyData}: { keyData: KeyData }) {
    const openKeyHistoryDialog = useKeyHistoryDialog();
    const actions = React.useContext(KeyTableActionContext);
    if (!actions) throw new Error('TableActionContext not found');
    const {problems} = actions;
    const problem = problems.getKeyProblem(keyData);

    const handleOpenDialog = useCallback((event: React.MouseEvent) => {
        openKeyHistoryDialog(keyData, {x: event.clientX, y: event.clientY});
    }, [keyData, openKeyHistoryDialog]);

    return (
        <IconButton
            size="small"
            onClick={handleOpenDialog}
            aria-label={`Key history for key ${keyData.keyNumber}`}
        >
            {problem ? (
                <KeyProblemIcon problem={problem} sx={{mt: .5}}/>
            ) : (
                <UpdateIcon fontSize="small"/>
            )}
        </IconButton>
    );
}

function KeyOperationButton({keyData}: { keyData: KeyData }) {
    const [open, setOpen] = useState(false);
    const keySnapshot = useMemo(
        () => {
            const record = activeRecord(keyData);
            return record ? {key: keyData, historyRecord: record} : undefined;
        },
        [keyData],
    );

    const handleOpenDialog = useCallback(() => {
        setOpen(true);
    }, [setOpen]);

    const handleCloseDialog = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    return (
        <>
            <Tooltip title={keySnapshot ? "Key operation" : "No active key record"}>
                <span>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={handleOpenDialog}
                        disabled={!keySnapshot}
                    >
                        <KeyOperationsIcon fontSize="small"/>
                    </IconButton>
                </span>
            </Tooltip>
            {keySnapshot && (
                <KeyOperationDialog
                    keySnapshot={keySnapshot}
                    open={open}
                    onClose={handleCloseDialog}
                />
            )}
        </>
    );
}

function KeyRowMenu({keyData}: { keyData: KeyData }) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const actions = React.useContext(KeyTableActionContext);
    if (!actions) throw new Error('TableActionContext not found');
    const {onEditKey, onDeleteKey} = actions;

    const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, [setAnchorEl]);

    const handleCloseMenu = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const handleEditKey = useCallback(() => {
        setAnchorEl(null);
        onEditKey(keyData);
    }, [onEditKey, keyData]);

    const handleDeleteKey = useCallback(() => {
        setAnchorEl(null);
        onDeleteKey(keyData);
    }, [onDeleteKey, keyData]);

    return (
        <>
            <IconButton size="small" onClick={handleOpenMenu}>
                <MoreVertIcon fontSize="small"/>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                <MenuItem onClick={handleEditKey}>
                    <ListItemIcon>
                        <EditIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Edit key</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDeleteKey}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error"/>
                    </ListItemIcon>
                    <ListItemText>Delete key</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}

function KeyOperationCell({row}: {
    row: KeyData,
}) {
    const context = useContext(KeyTableActionContext);
    if (!context) return null;

    return (
        <TableCell align="right">
            <ButtonGroup>
                {context.canOperate && <KeyOperationButton keyData={row}/>}
                {context.canAdmin && <KeyRowMenu keyData={row}/>}
            </ButtonGroup>
        </TableCell>
    );
}

const columns: TableColumnData<KeyData>[] = [
    {
        key: 'key-number',
        header: 'Key',
        width: '8em',
        cell: row => {
            const record = activeRecord(row);
            return (
                <TableCell>
                    <KeyChip keyData={row} record={record}/>
                </TableCell>
            );
        }
    },
    {
        key: 'location',
        header: 'Location',
        width: '20em',
        cell: row => {
            const record = activeRecord(row);
            return (
                <TableCell>
                    {record && (
                        <KeyLocationShield location={record.location}/>
                    )}
                </TableCell>
            );
        },
    },
    {
        key: 'history',
        header: 'History',
        width: '17em',
        cell: row => (
            <TableCell>
                <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between">
                    <KeyHistoryPeriod record={activeRecord(row)}/>
                    <KeyErrorCell keyData={row}/>
                </Stack>
            </TableCell>
        )
    },
    {
        key: 'notes',
        header: 'Notes',
        cell: row => {
            const record = activeRecord(row);
            return (
                <TableCell>
                    <Stack direction="row" gap={1} alignItems="baseline" flexWrap="wrap">
                        {row.tags.map(tag => (
                            <KeyTagChip keyTag={tag} key={tag.id} size="small"/>
                        ))}
                        {row.transponderId && (
                            <Typography variant="caption" component="span" color="text.secondary">
                                NFC: {row.transponderId}
                            </Typography>
                        )}
                        {record?.notes && (
                            <Typography variant="body2" component="span" color="text.secondary">
                                {record.notes}
                            </Typography>
                        )}
                    </Stack>

                </TableCell>
            );
        }
    },
    {
        key: 'operation',
        header: '',
        width: '88px',
        cell: row => (
            <KeyOperationCell row={row}/>
        )
    },
];

type KeyTableActions = {
    problems: KeyProblems,
    canOperate: boolean,
    canAdmin: boolean,
    onEditKey: (key: KeyData) => void;
    onDeleteKey: (key: KeyData) => void;
}

export const KeyTableActionContext = createContext<KeyTableActions | undefined>(undefined);

export function KeyTable(props: {
    keys: KeyData[];
}) {
    return (
        <VirtualTable
            columns={columns}
            rows={props.keys}
            id={idFn}
        />
    )
}
